import React, { useState, useContext, useEffect } from "react";
import { Box, Button, Typography } from "@mui/material";
import { Add } from "@mui/icons-material";
import { ThemeContext } from "../../context/ThemeContext";
import { useAuthContext } from "../../hooks/contexts/useAuthContext";
import axios from "axios";
import { getColors } from "../../themes/theme";
import { AutomationItem } from "../../components/automation/AutomationItem";
import { AutomationDialog } from "../../components/automation/AutomationDialog";
import { useSnackbar } from "notistack";
import { AddCustomVideoDialog } from "../../components/automation/AddCustomVideoDialog";

const AutomationPage = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { token, user } = useAuthContext();
  const { darkMode } = useContext(ThemeContext);
  const [allAutomations, setAllAutomations] = useState([]);
  const [addVideoLoopDialogOpen, setAddVideoLoopDialogOpen] = useState(false);
  const [addCustomVideoDialogOpen, setAddCustomVideoDialogOpen] =
    useState(false);
  const [videoLoopDialogData, setVideoLoopDialogData] = useState(null);

  const handleAddClicked = () => {
    setVideoLoopDialogData({ videoLoopName: "", selectedVideos: [] });
    setAddVideoLoopDialogOpen(true);
  };

  const handleAddCustomVideoClicked = () => {
    setAddCustomVideoDialogOpen(true);
  };

  const handleSaveAutomation = async (data) => {
    try {
      if (data?.id) {
        // Modify existing automation
        const response = await axios.put(
          `${process.env.REACT_APP_API_URL}/video-loop/${data.id}`,
          data,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        enqueueSnackbar("Successfully Changed", { variant: "success" });
        setAllAutomations((prev) =>
          prev
            .map((automation) =>
              automation.id === data.id ? response.data : automation
            )
            .sort((a, b) => a.loopname.localeCompare(b.loopname))
        );
      } else {
        // Add new automation
        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}/video-loop`,
          data,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        enqueueSnackbar("Successfully Added", { variant: "success" });
        setAllAutomations((prev) => {
          const updatedAutomations = [...prev, response.data];
          return updatedAutomations.sort((a, b) =>
            a.loopname.localeCompare(b.loopname)
          );
        });
      }
    } catch (err) {
      console.error("Error saving automation:", err);
    }
  };

  useEffect(() => {
    if (!user || !token) return;
    const fetchAllAutomations = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/video-loop/practiceid/${user?.practiceId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setAllAutomations(response.data);
      } catch (err) {
        console.error("Error fetching all automations:", err);
      }
    };
    fetchAllAutomations();
  }, [user, token]);

  useEffect(() => {
    //
  }, []);

  return (
    <Box
      px={{ xs: "10px", sm: 3, md: "30px", lg: 4, xl: 8 }}
      py={{ xs: 4, sm: 3, md: "30px", lg: 4, xl: 8 }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          flexDirection: { xs: "column", sm: "row" },
          // alignItems: "center",
          mb: 5,
        }}
      >
        <Typography
          sx={{
            fontFamily: "Poppins",
            fontSize: { xs: "24px", sm: "28px" },
            fontWeight: 500,
            color: darkMode
              ? getColors.drawerTextDark
              : getColors.thumbnailTextLight,
          }}
        >
          Video Automation
        </Typography>
        <Box
          gap={2}
          display="flex"
          flexDirection="row"
          justifyContent="flex-end"
        >
          <Button
            variant="contained"
            startIcon={<Add />}
            sx={{
              mt: { xs: 2, sm: 0 },
              alignSelf: "flex-end",
              fontFamily: "Poppins",
              fontSize: { xs: "16px", sm: "18px" },
              px: { xs: "12px", sm: "16px" },
              py: { xs: "8px", sm: "12px" },
              background: darkMode ? "#2D2E30" : "#E6E6E6",
              color: darkMode
                ? getColors.primaryTextDark
                : getColors.secondaryTextLight,
              "&:hover": {
                color: getColors.primaryTextDark,
              },
            }}
            onClick={handleAddCustomVideoClicked}
          >
            New Custom Video
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            sx={{
              mt: { xs: 2, sm: 0 },
              alignSelf: "flex-end",
              fontFamily: "Poppins",
              fontSize: { xs: "16px", sm: "18px" },
              px: { xs: "12px", sm: "16px" },
              py: { xs: "8px", sm: "12px" },
              background: darkMode ? "#2D2E30" : "#E6E6E6",
              color: darkMode
                ? getColors.primaryTextDark
                : getColors.secondaryTextLight,
              "&:hover": {
                color: getColors.primaryTextDark,
              },
            }}
            onClick={handleAddClicked}
          >
            New Video Loop
          </Button>
        </Box>
      </Box>

      <Box
        display="grid"
        sx={{
          gridTemplateColumns: {
            xs: "repeat(1, 1fr)",
            sm: "repeat(2, 1fr)",
            md: "repeat(2, 1fr)",
            lg: "repeat(3, 1fr)",
            xl: "repeat(4, 1fr)",
          },
        }}
        gap={5}
      >
        {allAutomations.map((automation, index) => (
          <AutomationItem
            key={index}
            {...automation}
            onSave={handleSaveAutomation}
            onDelete={(id) =>
              setAllAutomations((prev) =>
                prev.filter((automation) => automation.id !== id)
              )
            }
          />
        ))}
      </Box>

      {/* Automation Dialog */}
      <AutomationDialog
        open={addVideoLoopDialogOpen}
        onClose={() => setAddVideoLoopDialogOpen(false)}
        onSave={handleSaveAutomation}
        initialData={videoLoopDialogData}
      />

      <AddCustomVideoDialog
        open={addCustomVideoDialogOpen}
        onClose={() => setAddCustomVideoDialogOpen(false)}
      />
    </Box>
  );
};

export default AutomationPage;
